import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiService } from '../services/api';
import InlineFeedback from './InlineFeedback';
import ConfirmDialog from './ConfirmDialog';
import { useFeedback } from '../hooks/useFeedback';
import CopyIconButton from './CopyIconButton';
import { getRelativeTime } from '../utils/dateTime';
import {
  ShareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface ShareDashboardURLProps {
  jobId: string;
}

export const ShareDashboardURL: React.FC<ShareDashboardURLProps> = ({ jobId }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'rotate' | 'revoke' | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const feedback = useFeedback();

  // Fetch existing share URL
  const { data: shareURL, isLoading, error, refetch } = useQuery({
    queryKey: ['shareURL', jobId],
    queryFn: () => apiService.getShareURL(jobId),
    retry: false,
  });

  // Create share URL mutation. Success needs no message: the toolbar swaps
  // to Copy link/Revoke and the password box appears.
  const createMutation = useMutation({
    mutationFn: () => apiService.createShareURL(jobId),
    onSuccess: (data) => {
      feedback.clear();
      setPassword(data.password);
      refetch();
    },
    onError: () => {
      feedback.showError('Failed to create share link.');
    },
  });

  // Revoke share URL mutation. Success shows as the toolbar swapping back
  // to the Share button.
  const revokeMutation = useMutation({
    mutationFn: () => apiService.revokeShareURL(jobId),
    onSuccess: () => {
      feedback.clear();
      setConfirmAction(null);
      setPassword(null);
      setShowPassword(false);
      refetch();
    },
    onError: () => {
      setConfirmError('Failed to revoke share link.');
    },
  });

  // Rotate password mutation. Success shows as the new password box
  // replacing the Rotate button.
  const rotatePasswordMutation = useMutation({
    mutationFn: () => apiService.rotateSharePassword(jobId),
    onSuccess: (data) => {
      feedback.clear();
      setConfirmAction(null);
      setPassword(data.password);
      setShowPassword(false);
    },
    onError: () => {
      setConfirmError('Failed to rotate password.');
    },
  });

  const openConfirm = (action: 'rotate' | 'revoke') => {
    setConfirmError(null);
    setConfirmAction(action);
  };

  const closeConfirm = () => {
    setConfirmAction(null);
    setConfirmError(null);
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        {error && !shareURL ? (
          // Only with no link loaded: a failed refetch must not hide working controls.
          <span className="text-sm text-red-400">Failed to load the share link.</span>
        ) : !shareURL ? (
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="btn-icon flex items-center gap-2"
            title="Create share link"
          >
            <ShareIcon className="h-4 w-4" />
            {createMutation.isPending ? 'Creating...' : 'Share'}
          </button>
        ) : (
          <>
            <CopyIconButton text={shareURL.share_url} label="link" className="btn-icon" />

            {/* Password section - show rotate button or password display */}
            {password ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-widget-bg rounded border border-secondary">
                <span className="text-xs text-secondary">Password:</span>
                {showPassword ? (
                  <span className="text-xs font-mono font-bold text-body-color px-1">
                    {password}
                  </span>
                ) : (
                  <span className="text-xs font-mono text-secondary px-1">••••••••••••</span>
                )}
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 hover:bg-body-bg/50 rounded transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-3.5 h-3.5 text-secondary" />
                  ) : (
                    <EyeIcon className="w-3.5 h-3.5 text-secondary" />
                  )}
                </button>
                <CopyIconButton
                  text={password}
                  label="password"
                  className="p-1 text-secondary hover:bg-body-bg/50 rounded transition-colors"
                  iconClassName="w-3.5 h-3.5"
                />
              </div>
            ) : (
              <button
                onClick={() => openConfirm('rotate')}
                disabled={rotatePasswordMutation.isPending}
                className="btn-icon flex items-center gap-2"
                title="Rotate password"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {rotatePasswordMutation.isPending ? 'Rotating...' : 'Rotate Password'}
              </button>
            )}

            {/* Delete button */}
            <button
              onClick={() => openConfirm('revoke')}
              disabled={revokeMutation.isPending}
              className="btn-icon flex items-center gap-2 text-red-500 hover:text-red-400"
              title={shareURL.expires_at
                ? `Expiry: ${getRelativeTime(shareURL.expires_at)}`
                : 'Link has no expiration date'
              }
            >
              <TrashIcon className="h-4 w-4" />
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke link'}
            </button>
          </>
        )}
      </div>
      <InlineFeedback feedback={feedback.feedback} />
      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction === 'rotate' ? 'Rotate Password' : 'Revoke Share Link'}
        message={
          confirmAction === 'rotate'
            ? 'This replaces the current password. Everyone with the old password loses access.'
            : 'This revokes the share link. Everyone with the link loses access. You cannot undo it.'
        }
        confirmLabel={confirmAction === 'rotate' ? 'Rotate Password' : 'Revoke Link'}
        danger={confirmAction === 'revoke'}
        pending={confirmAction === 'rotate' ? rotatePasswordMutation.isPending : revokeMutation.isPending}
        error={confirmError}
        onConfirm={() =>
          confirmAction === 'rotate' ? rotatePasswordMutation.mutate() : revokeMutation.mutate()
        }
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default ShareDashboardURL;
